import { SxProps, Typography } from "@mui/material";
import { PropsWithChildren, ReactNode } from "react";
import { Box, Icon, Link } from "../../common/components";
import { MaterialSymbolType } from "../../common/components/Icon/IconConstant";
import { createStyles, palette } from "../../styles/theme";
import { PropsWithDataTest } from "../../types";
import { mergeSx } from "../../utils/themeUtils";

const styles = createStyles({
  container: {
    background: palette.misc.warningBg,
    color: palette.misc.warningText,
    borderRadius: "8px",
    display: "flex",
    padding: "16px",
    mt: "5px",
  },
  success: {
    background: palette.misc.successBg,
    color: palette.misc.successText,
    border: `1px solid ${palette.misc.successBorder}`,
    "& span": {
      color: palette.misc.successText,
    },
  },
  warning: {
    background: palette.misc.warningBg,
    color: palette.misc.warningText,
    border: `1px solid ${palette.misc.warningBorder}`,
    "& span": {
      color: palette.misc.warningText,
    },
  },
  alert: {
    background: palette.misc.errorBg,
    color: palette.misc.errorText,
    border: `1px solid ${palette.misc.errorBorder}`,
    "& span": {
      color: palette.misc.errorText,
    },
  },
  info: {
    background: palette.grey.lightBackground,
    color: palette.grey.primary,
    border: `1px solid ${palette.grey.lightGrey}`,
  },
});

export enum MessageTypes {
  SUCCESS = "success",
  WARNING = "warning",
  ALERT = "alert",
  INFO = "info",
}

interface Props extends PropsWithDataTest<PropsWithChildren> {
  title?: string;
  message?: string | ReactNode;
  linkText?: string;
  linkUrl?: string;
  titleIcon?: MaterialSymbolType;
  messageIcon?: MaterialSymbolType;
  fullWidth?: boolean;
  type?: MessageTypes;
  dataTest?: string;
  sx?: SxProps;
}

export const Message = (props: Props) => {
  const {
    title,
    message,
    linkText,
    linkUrl,
    titleIcon = null,
    messageIcon = null,
    fullWidth = true,
    type = MessageTypes.WARNING,
    "data-test": dataTest,
    sx,
  } = props;

  return (
    <Box
      fullWidth={fullWidth}
      sx={mergeSx(styles.container, styles[type], sx)}
      data-test={dataTest}
    >
      {title && (
        <Box display={"flex"} alignItems={"center"} gap={"12px"}>
          {titleIcon && <Icon icon={titleIcon} size={24} />}
          <Typography variant="textRegular">{title}</Typography>
        </Box>
      )}
      <Box>
        {message && (
          <Box display={"flex"} alignItems={"center"} gap={"12px"}>
            {messageIcon && <Icon icon={messageIcon} size={24} />}
            {typeof message === "string" ? (
              <Typography variant="textRegular">{message}</Typography>
            ) : (
              <Box>{message}</Box>
            )}
          </Box>
        )}
        {linkUrl && (
          <Link to={linkUrl} target={"_self"} rel="noreferrer">
            {linkText || linkUrl}
          </Link>
        )}
      </Box>
      {props.children}
    </Box>
  );
};

export default Message;
